import React, { useState } from "react";
import Input from "../../components/common/input/Input";
import Button from "../../components/common/button/Button";
import Backbutton from "../../components/common/backbutton/Backbutton";
import { usePasswordResetInstance } from "../../utils/userInstance/usePasswordResetInstance";
import { useSelector } from "react-redux";
import CryptoJS from "crypto-js";

const Setpassword = () => {
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);         // NEW - for password visibility
    const [showConfirmPassword, setShowConfirmPassword] = useState(false); // NEW - for confirm password visibility
    const [errors, setErrors] = useState({
        password: "",
        confirmPassword: ""
    });
    const {resetPassword} = usePasswordResetInstance({});
    const {UserEmail} = useSelector(state => state.user);
    const {isUsermanagementLoader} = useSelector(state => state.loader);

    const generatePasswordHash = (password) => {
        const rawKey = "A7VCsRP/mkFmGQrycUixJ26dQ6oft0HymEfocEJjIoI=";
        const keyBytes = CryptoJS.enc.Base64.parse(rawKey);
        const passwordBytes = CryptoJS.enc.Utf8.parse(password);
        const combinedBytes = passwordBytes.clone().concat(keyBytes);
        const sha256Hash = CryptoJS.SHA256(combinedBytes);
        return CryptoJS.enc.Base64.stringify(sha256Hash);
    }

    /* ───────────────── password visibility toggles ───────────────── */
    const togglePasswordVisibility = () => setShowPassword(!showPassword);
    const toggleConfirmPasswordVisibility = () => setShowConfirmPassword(!showConfirmPassword);

    /* ───────────────── password validation helpers ───────────────── */
    const validatePassword = (password) => {
        if (!password) return "Password is required";
        if (password.length < 8) return "Password must be at least 8 characters long";
        if (password.length > 128) return "Password must be less than 128 characters";
        if (!/(?=.*[a-z])/.test(password)) return "Password must contain at least one lowercase letter";
        if (!/(?=.*[A-Z])/.test(password)) return "Password must contain at least one uppercase letter";
        if (!/(?=.*\d)/.test(password)) return "Password must contain at least one number";
        if (!/(?=.*[@$!%*?&])/.test(password)) return "Password must contain at least one special character (@$!%*?&)";
        if (/\s/.test(password)) return "Password cannot contain spaces";
        
        // Check for common weak patterns
        const commonPatterns = [
          /(.)\1{2,}/, // 3+ repeated characters
          /123456|654321|abcdef|qwerty|password/i, // common sequences
        ];
        
        for (let pattern of commonPatterns) {
          if (pattern.test(password)) {
            return "Password contains common patterns. Please choose a stronger password";
          }
        }
        
        return "";
    };

    const validateConfirmPassword = (confirmPassword, password) => {
        if (!confirmPassword) return "Please confirm your password";
        if (confirmPassword !== password) return "Passwords do not match";
        return "";
    };

    const handleInputChange = (e, field) => {
        let { value } = e.target;
        
        if (field === "password") {
            setPassword(value);
            const passwordError = validatePassword(value);
            const confirmPasswordError = confirmPassword ? validateConfirmPassword(confirmPassword, value) : "";
            setErrors({ 
                ...errors, 
                password: passwordError,
                confirmPassword: confirmPasswordError
            });
        }

        if (field === "confirmPassword"){
            setConfirmPassword(value);
            setErrors({ 
                ...errors, 
                confirmPassword: validateConfirmPassword(value, password) 
            });
        }
    };

    const validateForm = () => {
        const formErrors = { 
            password: "", 
            confirmPassword: ""
        };

        // Run all validations
        formErrors.password = validatePassword(password);
        formErrors.confirmPassword = validateConfirmPassword(confirmPassword, password);

        setErrors(formErrors);
        
        // Return true if no errors
        return Object.values(formErrors).every(error => error === "");
    };

    const handleLogin = (e) => {
        e.preventDefault();
        if (validateForm()) {
            const encryptedPassword = generatePasswordHash(password)
            resetPassword({email:UserEmail, password:encryptedPassword})
        }
    };

    return (
        <div className="bg-grey-200 w-full h-full lg:w-2/4 flex flex-col justify-center items-center p-5" style={{margin:'auto'}}>
            <div className="w-full min-w-[200px] xs:max-w-[320px] sm:max-w-[420px]">
                <div>
                    <p className="text-2xl font-bold text-center lg:text-start">Set your password</p>
                    <p className="text-sm font-medium mt-2 text-center lg:text-start text-gray-500">
                        Set your password to access Campus Crow
                    </p>
                </div>
                <div className="mt-5 relative">
                    <Input
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        required={true}
                        label="Password"
                        value={password}
                        onChange={(e) => handleInputChange(e, "password")} 
                        error={errors.password} 
                        fullWidth={true}
                    />
                    <button
                        type="button"
                        className="absolute right-3 top-9 text-gray-500 hover:text-gray-700 focus:outline-none"
                        onClick={togglePasswordVisibility}
                    >
                        {showPassword ? (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                            </svg>
                        ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                        )}
                    </button>
                </div>
                <div className="mt-5 relative">
                    <Input
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Enter your confirm password"
                        required={true}
                        label="Confirm password"
                        value={confirmPassword}
                        onChange={(e) => handleInputChange(e, "confirmPassword")} 
                        error={errors.confirmPassword} 
                        fullWidth={true}
                    />
                    <button
                        type="button"
                        className="absolute right-3 top-9 text-gray-500 hover:text-gray-700 focus:outline-none"
                        onClick={toggleConfirmPasswordVisibility}
                    >
                        {showConfirmPassword ? (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                            </svg>
                        ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                        )}
                    </button>
                </div>
                <p className="text-xs text-red-500 mt-1">
                     Password must be at least 8 characters long and include at least one uppercase letter, one lowercase letter, one number, and one special character.
                </p>
                <div className="mt-8">
                    <Button
                        label={isUsermanagementLoader ?
                            <div className="flex justify-center">
                                <svg class="animate-spin h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            </div>
                            :"Reset password"}
                        varient="contained"
                        color="primary"
                        fullWidth={true}
                        onClick={handleLogin} 
                        disabled={isUsermanagementLoader}
                    />
                </div>
                <Backbutton />
            </div>
        </div>
    );
};

export default Setpassword;